import type { StatusLevel } from './StatusPill';

export type FloorplanAsset = {
  id: string;
  name: string;
  severity: StatusLevel;
  x: number; // 0..1 normalized
  y: number; // 0..1 normalized
};

export type FloorplanCanvasProps = {
  site: { id: string; name: string; floorplanUrl?: string };
  assets: FloorplanAsset[];
  onSelect?: (assetId: string) => void;
  /** Width in CSS px. Height is computed from the 16:9 aspect. */
  width?: number;
};

const SEVERITY_FILL: Record<StatusLevel, string> = {
  critical: 'var(--color-critical)',
  warn: 'var(--color-warn)',
  watch: 'var(--color-watch)',
  healthy: 'var(--color-healthy)',
  info: 'var(--color-info)',
};

/**
 * Floorplan-style canvas: SVG with the floorplan as a background
 * <image>, or a hatched placeholder when floorplanUrl is empty. Asset
 * glyphs sit at normalized (x, y) coordinates so a layout works for
 * any image aspect. Clicking a glyph fires onSelect — the parent
 * routes to /assets/$id. Pan/zoom is deliberately not implemented
 * yet: the canvas defaults to a fit-to-container view sized for the
 * typical PDM plant footprint (~40 assets per floor).
 */
export function FloorplanCanvas({
  site,
  assets,
  onSelect,
  width = 720,
}: FloorplanCanvasProps) {
  const height = Math.round((width * 9) / 16);
  const viewBox = `0 0 ${width} ${height}`;

  return (
    <figure
      className="flex flex-col gap-sp-2"
      data-site-id={site.id}
      aria-label={`Floorplan for ${site.name}`}
    >
      <svg
        viewBox={viewBox}
        width={width}
        height={height}
        className="rounded-r-card border border-gray-300 bg-gray-50"
        role="img"
        aria-label={`Floorplan of ${site.name} with ${assets.length} asset glyphs`}
      >
        <defs>
          <pattern
            id={`hatch-${site.id}`}
            patternUnits="userSpaceOnUse"
            width="8"
            height="8"
            patternTransform="rotate(45)"
          >
            <line x1="0" y1="0" x2="0" y2="8" stroke="var(--color-gray-300)" strokeWidth="1" />
          </pattern>
        </defs>
        {site.floorplanUrl ? (
          <image
            href={site.floorplanUrl}
            x="0"
            y="0"
            width={width}
            height={height}
            preserveAspectRatio="xMidYMid slice"
          />
        ) : (
          <rect
            x="0"
            y="0"
            width={width}
            height={height}
            fill={`url(#hatch-${site.id})`}
          />
        )}
        {assets.map((a) => {
          const cx = Math.max(8, Math.min(width - 8, a.x * width));
          const cy = Math.max(8, Math.min(height - 8, a.y * height));
          return (
            <g
              key={a.id}
              transform={`translate(${cx} ${cy})`}
              data-asset-id={a.id}
              data-severity={a.severity}
              role="button"
              tabIndex={0}
              aria-label={`${a.name} · ${a.severity}`}
              onClick={onSelect ? () => onSelect(a.id) : undefined}
              onKeyDown={
                onSelect
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(a.id);
                      }
                    }
                  : undefined
              }
              className="cursor-pointer"
            >
              <circle r="10" fill={SEVERITY_FILL[a.severity]} stroke="var(--color-gray-900)" strokeWidth="1" />
              <text
                y="-14"
                textAnchor="middle"
                fontSize="10"
                fontFamily="var(--font-family-font-mono)"
                fill="var(--color-gray-900)"
              >
                {a.name}
              </text>
            </g>
          );
        })}
        {assets.length === 0 ? (
          <text
            x={width / 2}
            y={height / 2}
            textAnchor="middle"
            fontFamily="var(--font-family-font-mono)"
            fontSize="12"
            fill="var(--color-gray-700)"
          >
            No assets at this site yet
          </text>
        ) : null}
      </svg>
      <figcaption className="text-fs-meta text-gray-700">
        {site.name} · {assets.length} asset{assets.length === 1 ? '' : 's'}
      </figcaption>
    </figure>
  );
}
